import { numeroBR, inteiroBR } from "./numero";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import * as A from "./api";
import { vincularAoChecklist, sincronizarFotos } from "./fotos";

/**
 * CAMADA DE DADOS
 *
 * Substitui o `useState({...SEED})` do protótipo por dados reais, sem
 * reescrever as doze telas.
 *
 * O protótipo passa `db` e `set` para cada tela. Aqui `db` vem da API e `set`
 * escreve no servidor — as assinaturas continuam iguais, então a conversão
 * acontece num lugar só em vez de espalhada por doze componentes.
 *
 * ESCOLHA ASSUMIDA: carrega o conjunto de trabalho inteiro na abertura.
 *
 * Uma oficina tem algumas centenas de clientes, veículos e peças — cerca de
 * 200 KB de JSON. Carregar tudo faz o app responder instantâneo, torna a
 * busca local e o modo offline sai de graça.
 *
 * O limite é real: acima de uns 2.000 registros por tabela isso fica pesado
 * e precisa virar paginação com busca no servidor. Para o piloto e para a
 * oficina de bairro, está certo. Para uma rede com dez unidades, não está.
 */

/* ── tradução entre o banco e as telas ────────────────────────────────── */

/**
 * O backend usa snake_case e nomes completos; o protótipo usa abreviações.
 * Traduzir aqui evita renomear campo em 3.800 linhas de tela — e evita a
 * pior alternativa, que seria mudar o banco para caber no protótipo.
 */
const deCliente = (c: any) => ({
  id: c.id, nome: c.nome, tipo: c.tipo === "juridica" ? "PJ" : "PF",
  doc: c.documento ?? "", tel: c.telefone ?? "", email: c.email ?? "",
  cidade: [c.municipio, c.uf].filter(Boolean).join(" / "),
  end: [c.logradouro, c.numero, c.bairro].filter(Boolean).join(", "),
  desde: (c.criado_em ?? "").slice(0, 10), obs: c.observacao ?? "", _v: c.versao,
});

const paraCliente = (c: any) => ({
  nome: c.nome, tipo: c.tipo === "PJ" ? "juridica" : "fisica",
  documento: (c.doc ?? "").replace(/\D/g, "") || null,
  telefone: c.tel || null, email: c.email || null,
  municipio: (c.cidade ?? "").split("/")[0]?.trim() || null,
  uf: (c.cidade ?? "").split("/")[1]?.trim() || null,
  logradouro: c.end || null, observacao: c.obs || null,
});

const deVeiculo = (v: any) => ({
  id: v.id, cli: v.cliente_id, placa: v.placa, marca: v.marca ?? "",
  modelo: v.modelo ?? "", ano: v.ano_modelo ?? v.ano_fabricacao ?? 0,
  cor: v.cor ?? "", km: v.km_atual ?? 0, comb: v.combustivel ?? "Flex",
  chassi: v.chassi ?? "", motor: v.codigo_motor ?? "", _v: v.versao,
});

const paraVeiculo = (v: any) => ({
  cliente_id: v.cli, placa: (v.placa ?? "").toUpperCase().replace(/[^A-Z0-9]/g, ""),
  marca: v.marca || null, modelo: v.modelo || null,
  /* `+"81.961"` dá 81,961 e o banco recusa, porque quilometragem é inteiro.
     Era esse o motivo de o carro não vincular ao cliente: a pessoa escrevia
     a quilometragem do jeito certo em português e o cadastro falhava. */
  ano_modelo: inteiroBR(v.ano), cor: v.cor || null,
  km_atual: inteiroBR(v.km) ?? 0, combustivel: v.comb || null,
  chassi: (v.chassi ?? "").toUpperCase() || null, codigo_motor: v.motor || null,
});

/**
 * `cat` é o NOME da categoria, resolvido a partir do id.
 *
 * A tabela guarda categoria_id e o `SELECT *` devolve o id, não o nome — então
 * `p.categoria` era sempre undefined e toda peça caía em "Geral". A tela usa
 * o nome para filtrar e agrupar, então a tradução acontece aqui, no mesmo
 * lugar que traduz o resto.
 */
const dePeca = (p: any, cats: Record<string, string> = {},
                fornecedores: Record<string, string> = {}) => ({
  id: p.id, cod: p.codigo_interno, nome: p.descricao,
  catId: p.categoria_id ?? null,
  cat: (p.categoria_id && cats[p.categoria_id]) || "Sem categoria",
  marca: p.marca ?? "", un: p.unidade ?? "UN",
  // quantidade menos reservado: é o que o balcão pode prometer, e é
  // diferente do que está na prateleira.
  qtd: Number(p.quantidade ?? 0) - Number(p.quantidade_reservada ?? 0),
  fisico: Number(p.quantidade ?? 0),
  min: Number(p.estoque_minimo ?? 0), max: Number(p.estoque_maximo ?? 0),
  custo: Number(p.custo_medio ?? 0), venda: Number(p.preco_venda ?? 0),
  local: p.localizacao ?? "",
  // `forn` é o NOME; a coluna guarda o id. Antes lia `p.fornecedor`, que não
  // existe no SELECT — por isso a coluna de fornecedor vivia vazia.
  fornId: p.fornecedor_padrao_id ?? null,
  forn: (p.fornecedor_padrao_id && fornecedores[p.fornecedor_padrao_id]) || "",
  ncm: p.ncm ?? "", _v: p.versao,
});

const paraPeca = (p: any) => ({
  // categoria_id faltava aqui: o formulário oferecia o campo, a pessoa
  // escolhia, e a escolha nunca saía do navegador.
  categoria_id: p.catId || null,
  fornecedor_padrao_id: p.fornId || null,
  codigo_interno: p.cod, descricao: p.nome, marca: p.marca || null,
  unidade: p.un || "UN",
  // Mesma história do preço: "1.250,00" precisa virar 1250, não 1,25.
  estoque_minimo: numeroBR(p.min) ?? 0, estoque_maximo: numeroBR(p.max),
  preco_venda: numeroBR(p.venda) ?? 0, localizacao: p.local || null, ncm: p.ncm || null,
});

/**
 * `os` é o número que a tela mostra ("OS-2608"), não o uuid.
 *
 * A tela de notas escreve "Ordem de serviço {n.os}" e compara com o mesmo
 * formato que `deOS` produz. Guardar o uuid aqui faria aparecer um
 * identificador que ninguém reconhece no balcão.
 */
const deNota = (n: any) => ({
  id: n.numero ? `${n.tipo === "nfse" ? "NF" : "NFe"}-${n.numero}` : String(n.id).slice(0, 8),
  _id: n.id,
  tipo: n.tipo,
  os: n.os_numero ? `OS-${n.os_numero}` : "—",
  osId: n.os_id,
  cli: n.cliente_id,
  data: (n.data_emissao ?? n.competencia ?? n.criado_em ?? "").slice(0, 10),
  valorServ: Number(n.valor_servicos ?? 0),
  valorPeca: Number(n.valor_pecas ?? 0),
  imposto: Number(n.valor_imposto ?? 0),
  status: n.status,
  chave: n.chave_acesso ?? n.codigo_verificacao ?? "",
  protocolo: n.protocolo ?? "—",
  pdf: n.link_pdf ?? null,
  mensagem: n.ultima_mensagem ?? "",
});

const dePedido = (p: any) => ({
  id: p.id, numero: Number(p.numero), status: p.status,
  fornId: p.fornecedor_id, forn: p.fornecedor_nome ?? "Sem fornecedor",
  fornTel: p.fornecedor_telefone ?? "", fornEmail: p.fornecedor_email ?? "",
  data: (p.data_pedido ?? "").slice(0, 10),
  prevista: (p.data_prevista ?? "")?.slice?.(0, 10) ?? "",
  recebido: (p.data_recebido ?? "")?.slice?.(0, 10) ?? "",
  total: Number(p.total ?? 0), itens: Number(p.itens ?? 0), obs: p.observacao ?? "",
});

const deServico = (s: any) => ({
  id: s.id, cod: s.codigo, nome: s.descricao,
  h: Number(s.tempo_horas ?? 0), valor: Number(s.valor ?? 0), _v: s.versao,
});

/** A máquina de estados do backend é mais rica que a do protótipo. */
/**
 * A condição do item do checklist tem dois nomes.
 *
 * A tela usa código curto porque o botão é do tamanho de um dedo. O banco
 * usa palavra inteira, num enum: bom, atencao, critico.
 *
 * Ninguém traduzia. O mecânico marcava os itens, o INSERT batia no enum e o
 * checklist inteiro voltava 422 — "um dos campos está num formato que o
 * sistema não entende", que não diz nada a quem está no balcão. Salvar só
 * funcionava sem marcar item nenhum, que é o oposto de um checklist.
 */
const CONDICAO_PARA_BANCO: Record<string, string> =
  { ok: "bom", warn: "atencao", crit: "critico" };
const CONDICAO_PARA_TELA: Record<string, string> =
  { bom: "ok", atencao: "warn", critico: "crit", nao_avaliado: "" };

const STATUS_PARA_TELA: Record<string, string> = {
  rascunho: "orcamento", orcamento: "orcamento",
  aguardando_aprovacao: "aguardando", aprovada: "aprovada",
  em_execucao: "execucao", finalizada: "finalizada",
  entregue: "entregue", recusada: "recusada", cancelada: "recusada",
};

const deOS = (o: any) => ({
  id: o.numero ? `OS-${o.numero}` : o.id, _id: o.id,
  cli: o.cliente_id, vei: o.veiculo_id, mec: o.mecanico_id,
  /* Cliente e veículo como o servidor já manda, pelo JOIN de GET /os. As
     telas procuram em db.clientes e db.veiculos, mas aquelas listas têm teto
     e vêm das mais recentes: carro antigo ainda no pátio pode não estar lá.
     Foi o que deixou o painel em branco em 16/10. Isto é o plano B. */
  placa: o.placa ?? "", marca: o.marca ?? "", modelo: o.modelo ?? "",
  clienteNome: o.cliente_nome ?? "",
  /* O nome que a tela mostra e edita. Vem do COALESCE do servidor: o que foi
     digitado na OS, e só na falta dele o nome do usuário vinculado. */
  mecNome: o.mecanico_nome ?? "",
  status: STATUS_PARA_TELA[o.status] ?? o.status,
  abertura: (o.data_abertura ?? "").slice(0, 10),
  previsao: (o.data_previsao ?? "").slice(0, 10),
  km: o.km_entrada ?? 0, queixa: o.queixa ?? "",
  desconto: Number(o.desconto_valor ?? 0),
  checklist: o.checklist_id, aprovadoEm: (o.data_aprovacao ?? "").slice(0, 10) || null,
  nfServico: null, nfPeca: null, assinatura: null,
  itens: (o.itens ?? []).map((i: any) => ({
    id: i.id, tipo: i.tipo, ref: i.peca_id ?? i.servico_id, desc: i.descricao,
    qtd: Number(i.quantidade), valor: Number(i.valor_unitario),
    custo: Number(i.custo_unitario ?? 0),
    // Quem fez ESTE item: numa OS com alinhamento e balanceamento, quem
    // alinhou pode não ser quem balanceou.
    mec: i.mecanico_anotado ?? "",
  })),
  /* Como ficou combinado o pagamento desta OS. Vem do servidor junto com a
     OS, e não de `db.fin`: aquela lista tem teto de 300 títulos e corta os
     mais recentes primeiro — a folha da OS de hoje sairia sem pagamento. */
  pagamento: (o.pagamento ?? []).map((p: any) => ({
    id: p.id,
    parcela: Number(p.parcela ?? 1), parcelas: Number(p.parcelas ?? 1),
    valor: Number(p.valor ?? 0), venc: (p.vencimento ?? "").slice(0, 10),
    forma: p.forma ?? null, pago: (p.quitado_em ?? "").slice(0, 10) || null,
  })),
  _v: o.versao,
});

const deOrcamento = (o: any) => ({
  id: o.numero ? `ORC-${String(o.numero).padStart(4, "0")}` : o.id, _id: o.id,
  cli: o.cliente_id, nome: o.nome_contato ?? "", tel: o.telefone_contato ?? "",
  vei: o.veiculo_id, veiculo: o.veiculo_descricao ?? "", status: o.status,
  criado: (o.criado_em ?? "").slice(0, 10), validade: o.validade_dias ?? 15,
  enviado: (o.enviado_em ?? "").slice(0, 10) || null,
  respondido: (o.respondido_em ?? "").slice(0, 10) || null,
  motivo: o.motivo_recusa, desconto: Number(o.desconto ?? 0),
  os: o.os_id, obs: o.observacao ?? "", _v: o.versao,
  // Mesma forma que a tela usa nos itens de OS: desc/qtd/valor, não as
  // colunas do banco. Repassar cru fazia a tela ler `i.desc` de um objeto
  // que só tem `descricao`, e a linha aparecia em branco.
  itens: (o.itens ?? []).map((i: any) => ({
    id: i.id, tipo: i.tipo, ref: i.peca_id ?? i.servico_id ?? null,
    desc: i.descricao, qtd: Number(i.quantidade ?? 0),
    valor: Number(i.valor_unitario ?? 0), custo: Number(i.custo_unitario ?? 0),
  })),
});

const deLancamento = (l: any) => ({
  id: l.id, tipo: l.tipo, desc: l.descricao, cli: l.cliente_id, os: l.os_id,
  valor: Number(l.valor), venc: (l.vencimento ?? "").slice(0, 10),
  pago: (l.quitado_em ?? "").slice(0, 10) || null,
  saldo: Number(l.saldo ?? l.valor), forma: l.forma_prevista ?? "pix",
});

const deMovimento = (m: any) => ({
  id: m.id, data: (m.criado_em ?? "").slice(0, 10), peca: m.peca_id,
  tipo: Number(m.quantidade) > 0 ? "entrada" : "saida",
  qtd: Math.abs(Number(m.quantidade)), motivo: m.motivo ?? "",
  valor: Number(m.custo_unitario ?? 0), resp: m.usuario_id,
});

/* ── contexto ─────────────────────────────────────────────────────────── */

interface Ctx {
  db: any;
  set: (fn: (d: any) => any) => void;
  recarregar: (o?: { silencioso?: boolean }) => Promise<void>;
  carregando: boolean;
  erro: string | null;
  acoes: ReturnType<typeof montarAcoes>;
}

const DadosCtx = createContext<Ctx | null>(null);
export const useDados = () => {
  const c = useContext(DadosCtx);
  if (!c) throw new Error("useDados fora do provedor");
  return c;
};

const VAZIO = {
  clientes: [], veiculos: [], pecas: [], servicos: [], usuarios: [],
  os: [], orcamentos: [], checklists: [], mov: [], fin: [], notas: [],
  oficina: null, categorias: [], fornecedores: [], pedidos: [],
};

export function ProvedorDados({ usuario, oficinas, children }: any) {
  const [db, setDb] = useState<any>(VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const vivo = useRef(true);

  const recarregar = useCallback(async (o: { silencioso?: boolean } = {}) => {
    if (!o.silencioso) setCarregando(true);
    try {
      /**
       * Tudo em paralelo. Em série seriam oito idas ao servidor uma depois
       * da outra — no 4G do galpão, a diferença entre abrir na hora e
       * parecer travado.
       */
      const [clientes, veiculos, pecas, servicos, os, orcamentos, fin, mov, cks, oficina, categorias, forns, peds, nfs] =
        await Promise.all([
          A.clientes.listar(), A.veiculos.listar(), A.pecas.listar(),
          A.servicos.listar(), A.os.listar(), A.orcamentos.listar(),
          A.financeiro.listar(), A.pecas.movimentos(), A.checklists.listar(),
          // Nome e telefone daqui vão para o orçamento que o cliente recebe
          // no WhatsApp. Se falhar, a mensagem sai sem contato em vez de sair
          // com o contato de outra empresa — que era o defeito anterior.
          A.oficina.ver().catch(() => null),
          A.categorias.listar().catch(() => []),
          A.fornecedores.listar().catch(() => []),
          A.pedidos.listar().catch(() => []),
          A.notas.listar().catch(() => []),
        ]);
      const mapaCats: Record<string, string> = Object.fromEntries(
        (categorias ?? []).map((c: any) => [c.id, c.nome]));
      const mapaForns: Record<string, string> = Object.fromEntries(
        (forns ?? []).map((f: any) => [f.id, f.nome]));

      if (!vivo.current) return;
      setDb({
        clientes: clientes.map(deCliente),
        veiculos: veiculos.map(deVeiculo),
        pecas: pecas.map((p: any) => dePeca(p, mapaCats, mapaForns)),
        servicos: servicos.map(deServico),
        usuarios: (oficinas?.equipe ?? [{ id: usuario.id, nome: usuario.nome,
          papel: "admin", cargo: "Proprietário",
          inic: usuario.nome.split(" ").slice(0, 2).map((p: string) => p[0]).join("") }]),
        // `nfServico` e `nfPeca` eram null fixo em `deOS`: a OS não sabia que
        // já tinha nota, e a lista de "OS concluídas sem nota" mostrava OS
        // que já tinham sido faturadas.
        os: os.map(deOS).map((o: any) => {
          const minhas = (nfs ?? []).filter((n: any) => n.os_id === o._id);
          const doTipo = (t: string) => {
            const n = minhas.find((x: any) => x.tipo === t && x.status !== "cancelada");
            return n ? deNota(n).id : null;
          };
          return { ...o, nfServico: doTipo("nfse"), nfPeca: doTipo("nfe") };
        }),
        orcamentos: orcamentos.map(deOrcamento),
        /* Itens e avarias vinham do servidor e eram jogados fora aqui:
            "fotos: [], avarias: [], itens: {}" fixos. Todo checklist salvo
            aparecia vazio na tela e a folha saía sem nada — o mecânico
            preenchia, salvava, reabria e não via o que tinha marcado. */
        checklists: cks.map((k: any) => ({
          id: k.id, os: null, vei: k.veiculo_id, data: (k.data ?? "").slice(0, 10),
          km: k.km ?? 0, comb: k.nivel_combustivel ?? 2, resp: k.responsavel_id,
          // A tela marca por nome do item; o banco guarda em linhas.
          itens: Object.fromEntries(
            (k.itens ?? []).filter((i: any) => i?.item && i.condicao !== "nao_avaliado")
              .map((i: any) => [i.item, CONDICAO_PARA_TELA[i.condicao] ?? i.condicao])),
          avarias: (k.avarias ?? []).map((a: any) => ({
            n: a.numero, x: a.pos_x, y: a.pos_y, tipo: a.tipo, desc: a.descricao ?? "",
          })),
          pertences: k.pertences ?? "",
          fotos: [], obs: k.observacao ?? "",
          assinado: !!k.assinado_em, _fotos: k.fotos ?? 0,
        })),
        mov: mov.map(deMovimento), fin: fin.map(deLancamento),
        notas: (nfs ?? []).map(deNota),
        oficina,
        categorias: categorias ?? [],
        fornecedores: forns ?? [],
        pedidos: (peds ?? []).map(dePedido),
      });
      setErro(null);
    } catch (e: any) {
      if (vivo.current) setErro(e?.message ?? "Não consegui carregar os dados.");
    } finally {
      if (vivo.current) setCarregando(false);
    }
  }, [usuario, oficinas]);

  useEffect(() => {
    vivo.current = true;
    recarregar();
    return () => { vivo.current = false; };
  }, [recarregar]);

  /**
   * Compatibilidade com as telas antigas.
   *
   * O protótipo chama `set(d => ...)` para mexer no estado. Aqui isso muda a
   * cópia local para a tela responder na hora, e as ações abaixo é que
   * escrevem no servidor. Sem isso, cada clique esperaria a ida e volta da
   * rede antes de mostrar qualquer coisa.
   */
  const set = useCallback((fn: (d: any) => any) => setDb((d: any) => fn(d)), []);

  const acoes = montarAcoes(setDb, recarregar);

  return (
    <DadosCtx.Provider value={{ db, set, recarregar, carregando, erro, acoes }}>
      {children}
    </DadosCtx.Provider>
  );
}

/* ── escrita ──────────────────────────────────────────────────────────── */

/**
 * Cada ação escreve no servidor e depois atualiza a cópia local com o que
 * ele devolveu — nunca com o que a tela imaginou.
 *
 * A diferença aparece em aprovar OS: o servidor reserva peça, cria título e
 * pode recusar por estoque. Assumir sucesso deixaria a tela mostrando uma
 * aprovação que não aconteceu.
 */
function montarAcoes(setDb: any, recarregar: (o?: any) => Promise<void>) {
  const chaves: Record<string, string> = {};
  const idem = (k: string) => (chaves[k] ??= crypto.randomUUID());

  return {
    async criarCliente(d: any) {
      const c = await A.clientes.criar(paraCliente(d));
      setDb((s: any) => ({ ...s, clientes: [deCliente(c), ...s.clientes] }));
      return deCliente(c);
    },

    async criarVeiculo(d: any) {
      const v = await A.veiculos.criar(paraVeiculo(d));
      setDb((s: any) => ({ ...s, veiculos: [...s.veiculos, deVeiculo(v)] }));
      return deVeiculo(v);
    },

    /**
     * Corrigir o cadastro do cliente — telefone que mudou, nome digitado
     * errado, endereço que faltava.
     *
     * A rota existe desde sempre e nenhuma tela chamava: dava para cadastrar
     * cliente e nunca mais mexer. Telefone errado numa oficina significa não
     * conseguir avisar que o carro ficou pronto.
     */
    async atualizarCliente(id: string, cliente: any) {
      const c = await A.clientes.atualizar(id, {
        ...paraCliente(cliente),
        ...(cliente?._v !== undefined ? { versao: cliente._v } : {}),
      });
      setDb((st: any) => ({
        ...st, clientes: st.clientes.map((x: any) => (x.id === id ? deCliente(c) : x)),
      }));
      return deCliente(c);
    },

    /**
     * Tira o veículo do cliente sem apagar nada.
     *
     * Carro vendido, carro lançado no cliente errado, locadora que devolveu a
     * frota. O veículo continua existindo com o histórico inteiro — as OS
     * dele apontam para a própria OS, não para este vínculo — e pode ser
     * ligado a outro dono depois. Apagar levaria junto a garantia e o que a
     * oficina já fez nele.
     */
    async desvincularVeiculo(id: string) {
      const v = await A.veiculos.atualizar(id, { cliente_id: null });
      setDb((st: any) => ({
        ...st, veiculos: st.veiculos.map((x: any) => (x.id === id ? deVeiculo(v) : x)),
      }));
    },

    /**
     * Corrigir o cadastro do veículo — placa errada, modelo trocado,
     * quilometragem que ninguém atualizou desde a compra.
     *
     * O app inteiro não tinha isso: dava para cadastrar carro e nunca mais
     * mexer nele. Placa digitada errada só se resolvia criando outro
     * veículo, e aí o histórico do carro nascia partido em dois.
     *
     * A versão vai junto quando existe: se alguém no balcão mexeu no mesmo
     * cadastro, o servidor recusa em vez de sobrescrever calado.
     *
     * Recebe o veículo inteiro, como criarVeiculo — as ações são montadas
     * sem acesso ao estado, e quem chama já tem a linha na mão.
     */
    async atualizarVeiculo(id: string, veiculo: any) {
      const v = await A.veiculos.atualizar(id, {
        ...paraVeiculo(veiculo),
        ...(veiculo?._v !== undefined ? { versao: veiculo._v } : {}),
      });
      setDb((st: any) => ({
        ...st, veiculos: st.veiculos.map((x: any) => (x.id === id ? deVeiculo(v) : x)),
      }));
      return deVeiculo(v);
    },

    /**
     * A OS foi aberta no carro errado. O servidor troca o dono junto, tirado
     * do próprio veículo, então recarrego tudo em vez de remendar aqui.
     */
    async trocarVeiculoOS(osId: string, veiculoId: string) {
      await A.api(`/os/${osId}`, { metodo: "PATCH", corpo: { veiculo_id: veiculoId } });
      await recarregar({ silencioso: true });
    },

    async criarCategoria(nome: string) {
      const c = await A.categorias.criar(nome);
      await recarregar({ silencioso: true });
      return c;
    },

    async criarServico(d: any) {
      const v = await A.servicos.criar({
        codigo: d.cod, descricao: d.nome, tempo_horas: +d.h || 0,
        valor: +d.valor || 0, codigo_lc116: d.lc116 || "14.01",
      });
      await recarregar({ silencioso: true });
      return v;
    },

    async criarPeca(d: any) {
      const p = await A.pecas.criar(paraPeca(d));
      // Recarrega em vez de inserir na lista: dePeca precisa do mapa de
      // categorias para resolver o nome, e inserir à mão faria a peça recém
      // criada aparecer como "Sem categoria" até o próximo carregamento.
      await recarregar({ silencioso: true });
      return p;
    },

    async salvarPeca(id: string, d: any) {
      // custo médio e quantidade não vão aqui de propósito: os dois são
      // mantidos por gatilho a partir do razão de movimentos. Mandar valor
      // direto faria o cadastro discordar do razão sem ninguém perceber.
      await A.pecas.atualizar(id, paraPeca(d));
      await recarregar({ silencioso: true });
    },

    async excluirPeca(id: string) {
      await A.pecas.excluir(id);
      await recarregar({ silencioso: true });
    },

    async criarFornecedor(nome: string) {
      const f = await A.fornecedores.criar({ nome });
      await recarregar({ silencioso: true });
      return f;
    },

    /** Um pedido por fornecedor: é assim que a compra acontece no balcão. */
    async gerarPedidos(grupos: Array<{ fornecedorId: string | null; itens: any[] }>) {
      const criados = [];
      for (const g of grupos) {
        criados.push(await A.pedidos.criar({
          fornecedorId: g.fornecedorId,
          itens: g.itens.map((i: any) => ({
            pecaId: i.id, quantidade: i.comprar, custoUnitario: i.custo,
          })),
        }));
      }
      await recarregar({ silencioso: true });
      return criados;
    },

    async marcarPedido(id: string, status: string) {
      await A.pedidos.status(id, status);
      await recarregar({ silencioso: true });
    },

    async receberPedido(id: string) {
      await A.pedidos.receber(id);
      await recarregar({ silencioso: true });
    },

    verPedido: (id: string) => A.pedidos.ver(id),

    async abrirOS(d: any) {
      const o = await A.os.abrir({
        clienteId: d.cli, veiculoId: d.vei, mecanicoId: d.mec,
        kmEntrada: +d.km || undefined, queixa: d.queixa, origem: "balcao",
      });
      await recarregar({ silencioso: true });
      return deOS(o);
    },

    async addItemOS(osId: string, item: any) {
      await A.os.addItem(osId, {
        tipo: item.tipo,
        pecaId: item.tipo === "peca" ? item.ref : undefined,
        servicoId: item.tipo === "servico" ? item.ref : undefined,
        descricao: item.desc, quantidade: item.qtd, valorUnitario: item.valor,
      });
      await recarregar({ silencioso: true });
    },

    /**
     * Aprovar é a operação mais delicada: move estoque e cria título.
     *
     * A chave é por OS, não por clique — o segundo toque na tela suja com
     * luva devolve o resultado do primeiro em vez de gerar dois boletos.
     */
    async aprovarOS(osId: string, canal = "presencial") {
      const r = await A.os.aprovar(osId, { canal }, idem(`aprovar:${osId}`));
      await recarregar({ silencioso: true });
      return r;
    },

    async executarOS(id: string) { await A.os.executar(id); await recarregar({ silencioso: true }); },
    async finalizarOS(id: string, diag?: string, pagamento?: any) {
      await A.os.finalizar(id, diag, pagamento);
      await recarregar({ silencioso: true });
    },
    async entregarOS(id: string) { await A.os.entregar(id); await recarregar({ silencioso: true }); },
    async recusarOS(id: string, m: string) { await A.os.recusar(id, m); await recarregar({ silencioso: true }); },
    async enviarOS(id: string) { await A.os.enviar(id); await recarregar({ silencioso: true }); },
    async reabrirOS(id: string) { await A.os.reabrir(id); await recarregar({ silencioso: true }); },
    /* Item de orçamento passou a existir no servidor. Antes a tela guardava
       a lista só no navegador e o PATCH salvava tudo menos os itens — o
       orçamento parecia pronto e sumia ao recarregar. */
    async addItemOrcamento(id: string, item: any) {
      await A.orcamentos.addItem(id, {
        tipo: item.tipo,
        pecaId: item.tipo === "peca" ? item.ref : undefined,
        servicoId: item.tipo === "servico" ? item.ref : undefined,
        descricao: item.desc, quantidade: item.qtd, valorUnitario: item.valor,
      });
      await recarregar({ silencioso: true });
    },

    async atualizarItemOrcamento(id: string, itemId: string, campos: any) {
      await A.orcamentos.atualizarItem(id, itemId, campos);
    },

    async removerItemOrcamento(id: string, itemId: string) {
      await A.orcamentos.removerItem(id, itemId);
      await recarregar({ silencioso: true });
    },

    async criarOrcamento(d: any) {
      const o = await A.orcamentos.criar({
        clienteId: d.cli, veiculoId: d.vei, nome: d.nome, telefone: d.tel,
        veiculo: d.veiculo, validadeDias: d.validade, desconto: d.desconto,
        observacao: d.obs,
      });
      await recarregar({ silencioso: true });
      return deOrcamento(o);
    },

    async aprovarOrcamento(id: string, placa: string) {
      const r = await A.orcamentos.aprovar(id, placa, idem(`orc:${id}`));
      await recarregar({ silencioso: true });
      return r;
    },

    async recusarOrcamento(id: string, motivo: string) {
      await A.orcamentos.recusar(id, motivo);
      await recarregar({ silencioso: true });
    },

    async entradaEstoque(d: any) {
      await A.pecas.entrada({
        pecaId: d.pecaId, quantidade: +d.qtd, custoUnitario: +d.custo,
        fornecedorId: d.fornecedorId, documento: d.documento,
      });
      await recarregar({ silencioso: true });
    },

    async ajustarInventario(pecaId: string, contagem: number, obs: string) {
      const r = await A.pecas.inventario(pecaId, contagem, obs);
      await recarregar({ silencioso: true });
      return r;
    },

    async baixarTitulo(id: string, valor: number, forma: string) {
      await A.financeiro.pagar(id, { valor, forma }, idem(`pag:${id}:${valor}`));
      await recarregar({ silencioso: true });
    },

    /** Equipe. Como a oficina, vive na tela que a usa. */
    verEquipe: () => A.equipe.listar(),
    convidarPessoa: (d: any) => A.equipe.convidar(d),
    alterarMembro: (id: string, d: any) => A.equipe.alterar(id, d),

    /**
     * Inteligência de estoque: reajuste de preço e mínimo sugerido.
     *
     * A tela chamava as duas e elas não existiam aqui — `acoes.reajustarPrecos
     * is not a function`. Os botões "Reajustar" e "Aplicar todos" quebravam.
     */
    async reajustarPrecos(itens: Array<{ pecaId: string }>) {
      const r = await A.estoqueInteligente.reajustar(itens);
      await recarregar({ silencioso: true });
      return r;
    },

    async aplicarMinimos(pecaIds: string[], automatizar = false) {
      const r = await A.estoqueInteligente.aplicarMinimos(pecaIds, automatizar);
      await recarregar({ silencioso: true });
      return r;
    },

    /** Cadastro da oficina. Não entra no `db`: é uma tela só que usa. */
    verOficina: () => A.oficina.ver(),
    salvarOficina: (d: Record<string, any>) => A.oficina.salvar(d),
    diagnosticoFiscal: () => A.fiscalApi.conexao(),
    cadastrarEmpresaFiscal: (d: any) => A.fiscalApi.cadastrarEmpresa(d),
    enviarCertificado: (c: string, s: string) => A.fiscalApi.enviarCertificado(c, s),
    registrarRetornoFiscal: () => A.fiscalApi.registrarRetorno(),
    ativarProducaoFiscal: () => A.fiscalApi.ativarProducao(),

    async criarLancamento(d: any) {
      await A.financeiro.criar({
        tipo: d.tipo, descricao: d.desc, valor: +d.valor,
        vencimento: d.venc, forma: d.forma, clienteId: d.cli,
      });
      await recarregar({ silencioso: true });
    },

    async salvarChecklist(d: any) {
      const ck = await A.checklists.criar({
        // O mecânico escreve "81.961". O + do JavaScript lê 81,961, a coluna
        // é inteira, e o checklist inteiro falha na gravação.
        veiculoId: d.vei, km: inteiroBR(d.km), combustivel: d.comb,
        pertences: d.pertences, observacao: d.obs,
        assinanteNome: d.assinado ? "assinado no aparelho" : null,
        itens: Object.fromEntries(Object.entries(d.itensPorGrupo ?? {}).map(
          ([grupo, itens]: any) => [grupo, Object.fromEntries(Object.entries(itens).map(
            ([item, cond]: any) => [item, CONDICAO_PARA_BANCO[cond] ?? cond]))])),
        avarias: d.avarias,
      }, idem(`ck:${d.vei}:${d.km}`));
      await recarregar({ silencioso: true });
      return ck;
    },

    async vincularFotos(checklistId: string) {
      await vincularAoChecklist(checklistId);
      // Tenta subir na hora; se não houver sinal, o observador cuida depois.
      sincronizarFotos().catch(() => {});
    },

    async importarEstoque(conteudoBase64: string, nome: string, mapeamento: any[]) {
      const est = await A.importacao.estagiar(conteudoBase64, nome, mapeamento);
      const resumo = await A.importacao.conferir(est.importacaoId);
      return { id: est.importacaoId, resumo,
               linhas: await A.importacao.linhas(est.importacaoId) };
    },

    async aplicarImportacao(id: string) {
      const r = await A.importacao.aplicar(id, idem(`imp:${id}`));
      await recarregar({ silencioso: true });
      return r;
    },

    /**
     * Editar e remover item do orçamento.
     *
     * A tela mudava quantidade e valor só no estado local — não havia rota. O
     * balconista corrigia o preço, via o número certo, e perdia tudo ao
     * recarregar. Pior que não deixar editar, porque parece que salvou.
     */
    async atualizarItemOS(osId: string, itemId: string, campos: any) {
      await A.os.atualizarItem(osId, itemId, campos);
      await recarregar({ silencioso: true });
    },

    async removerItemOS(osId: string, itemId: string) {
      await A.os.removerItem(osId, itemId);
      await recarregar({ silencioso: true });
    },

    /** Só para OS que nunca virou nada. O servidor recusa o resto. */
    async excluirOS(osId: string) {
      await A.os.excluir(osId);
      await recarregar({ silencioso: true });
    },

    async cancelarOS(osId: string, motivo: string) {
      const r = await A.os.cancelar(osId, motivo);
      await recarregar({ silencioso: true });
      return r;
    },

    /** Edições de campo: salvas com atraso para não virar uma requisição por tecla. */
    async atualizarOS(osId: string, campos: any) {
      await A.api(`/os/${osId}`, { metodo: "PATCH", corpo: campos });
    },
    async atualizarOrcamento(id: string, campos: any) {
      await A.api(`/orcamentos/${id}`, { metodo: "PATCH", corpo: campos });
    },

    async emitirNota(osId: string, tipo: "nfse" | "nfe") {
      const r = await A.fiscalApi.emitir(osId, tipo, idem(`nf:${osId}:${tipo}`));
      await recarregar({ silencioso: true });
      return r;
    },
  };
}
